/**
 * Vehicules: placement, pilotage, ancre, peche a la ligne et relecture d'une
 * sauvegarde.
 *
 * Module PUR: ni DOM, ni three.js, ni Math.random, ni Date.now. Le rendu
 * (client/render/vehicules.ts) ne fait que suivre ce que ce fichier decide.
 *
 * La regle qui structure tout le fichier est l'INVARIANT DE MILIEU:
 *
 *   le centre, les quatre coins et les deux extremites (proue/poupe,
 *   pare-chocs avant/arriere) d'un vehicule doivent TOUS se trouver dans son
 *   milieu — terre ferme pour la voiture, eau pour la barque.
 *
 * Le meme predicat (`positionValide`) sert au placement, au pilotage et a la
 * derive. Une voiture ne peut donc jamais entrer dans l'eau et une barque ne
 * peut jamais monter a terre, quel que soit le chemin emprunte et quel que soit
 * le nombre de pas de simulation: il n'existe pas un seul endroit du code ou une
 * position est acceptee sans passer par la.
 *
 * Consequence a connaitre: quand le pilote sort du milieu du vehicule, ce n'est
 * pas le vehicule qui suit, c'est le pilote qu'on ramene (`MajVehicule.pos`).
 */
import { CARTE_X, CARTE_Z, MAX_WALK_SLOPE, MER_OUEST, PLAYER_RADIUS, REACH, SEA_LEVEL } from '../constants.ts'
import type { Cout, Inventaire, ItemId } from '../types/items.ts'
import type { Batiment, Vehicule, VehiculeKind } from '../types/monde.ts'
import type { Joueur } from '../types/player.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { OBJETS } from '../data/objets.ts'
import { couleurCompte } from '../data/proprietaires.ts'
import { VEHICULES } from '../data/vehicules.ts'
import { hache2, type Alea } from './alea.ts'
import {
  MARGE_BATIMENT,
  RECUL_PARKING,
  dansEmprise,
  pisteDe,
  pointPiste,
  reperePiste,
  surLaPiste,
  type Piste,
} from './batiments.ts'
import { manquant, peutPayer } from './inventaire.ts'

/** Reponse de toutes les verifications: `raison` est affichable telle quelle. */
export interface ValidationVehicule {
  ok: boolean
  /** Message francais avec ses accents, pret pour le HUD. */
  raison?: string
}

/** Marge libre exigee autour d'un vehicule, en metres. */
export const MARGE_VEHICULE = 0.6

/** Pente moyenne maximale sous une voiture qu'on pose. */
const PENTE_POSE_MAX = 0.34

/** Denivele maximal sous une voiture qu'on pose, en metres. */
const DENIVELE_POSE_MAX = 1.3

/**
 * Vitesse de derive d'une barque laissee sans ancre, en m/s. Volontairement
 * minuscule: 5.4 m par minute reelle. On retrouve sa barque un peu plus loin,
 * jamais a l'autre bout du monde.
 */
export const DERIVE_VITESSE = 0.09

/** Distance de debarquement autour d'un vehicule, en metres. */
const PORTEE_DEBARQUEMENT = 2.6

/** Nombre de directions sondees pour trouver ou debarquer. */
const DIRECTIONS_DEBARQUEMENT = 16

/**
 * Pente maximale du sol ou l'on accepte de mettre pied a terre. Un peu sous
 * MAX_WALK_SLOPE: on ne depose pas le bonhomme sur une pente qu'il ne pourrait
 * meme pas remonter.
 */
const PENTE_DEBARQUEMENT_MAX = MAX_WALK_SLOPE * 0.8

// ------------------------------------------------------------------ geometrie

/**
 * Tourne un point local de `rot` radians. Convention du jeu: rot = 0 regarde
 * vers +z, comme `Joueur.rot`, et la meme valeur passe telle quelle dans
 * `rotation.y` du groupe three.js.
 */
export function tournerLibre(dx: number, dz: number, rot: number): Vec2 {
  const c = Math.cos(rot)
  const s = Math.sin(rot)
  return { x: dx * c + dz * s, z: -dx * s + dz * c }
}

/** Rotation inverse: du repere du monde vers celui du vehicule. */
export function tournerLibreInverse(dx: number, dz: number, rot: number): Vec2 {
  return tournerLibre(dx, dz, -rot)
}

/** Demi-diagonale de l'emprise: rayon du disque qui contient le vehicule. */
export function rayonVehicule(kind: VehiculeKind): number {
  const t = VEHICULES[kind].taille
  return Math.hypot(t.l, t.p) / 2
}

/**
 * Distance maximale entre le centre d'un vehicule et le point ou son pilote
 * pose le pied: c'est le rayon que sonde `pointDebarquement`. Le serveur s'en
 * sert pour accepter — ou non — la position annoncee a la descente.
 */
export function porteeDebarquement(kind: VehiculeKind): number {
  return rayonVehicule(kind) + PORTEE_DEBARQUEMENT
}

/**
 * Points testes par l'invariant de milieu: un quadrillage de 3 x 5 points sur
 * l'emprise (coins, milieux des cotes, proue, poupe et centre compris).
 *
 * Le pas de ce quadrillage n'est pas cosmetique. Avec seulement le centre et les
 * coins, l'ecart entre deux points vaut la moitie de la longueur, soit 2.1 m pour
 * la voiture: un obstacle plus mince que ca — et une case de navigation en fait
 * exactement 2 (NAV_CELL) — se glisse entre deux sondes et la voiture le
 * traverse. Ici le pas vaut p/4 = 1.05 m en long et l/2 = 1 m en large: rien de
 * la taille d'une case ne peut passer entre les mailles.
 */
export function pointsVehicule(kind: VehiculeKind, pos: Vec2, rot: number): Vec2[] {
  const t = VEHICULES[kind].taille
  const points: Vec2[] = []
  for (const fz of [-0.5, -0.25, 0, 0.25, 0.5]) {
    for (const fx of [-0.5, 0, 0.5]) {
      const v = tournerLibre(fx * t.l, fz * t.p, rot)
      points.push({ x: pos.x + v.x, z: pos.z + v.z })
    }
  }
  return points
}

/** Vrai si (x, z) est dans l'emprise du vehicule, elargie de `marge`. */
export function dansEmpriseVehicule(
  kind: VehiculeKind,
  pos: Vec2,
  rot: number,
  x: number,
  z: number,
  marge = 0,
): boolean {
  const t = VEHICULES[kind].taille
  const local = tournerLibreInverse(x - pos.x, z - pos.z, rot)
  return Math.abs(local.x) <= t.l / 2 + marge && Math.abs(local.z) <= t.p / 2 + marge
}

/** Distance du point (x, z) a la coque du vehicule. 0 si le point est dessus. */
export function distanceVehicule(v: Vehicule, x: number, z: number): number {
  const t = VEHICULES[v.kind].taille
  const local = tournerLibreInverse(x - v.pos.x, z - v.pos.z, v.rot)
  const dx = Math.max(Math.abs(local.x) - t.l / 2, 0)
  const dz = Math.max(Math.abs(local.z) - t.p / 2, 0)
  return Math.hypot(dx, dz)
}

/** Vrai si toute l'emprise tient dans le monde. */
export function dansLeMonde(kind: VehiculeKind, pos: Vec2, rot: number): boolean {
  // A l'ouest, la mer ouverte (MER_OUEST), pour ce qui va SUR L'EAU: c'est la
  // que la barque fait demi-tour, la cote de l'ile 1 etant a quatre metres de
  // x = 0. Une voiture ou un avion gardent le bord de la carte.
  const xMin = VEHICULES[kind].milieu === 'eau' ? -MER_OUEST : 0
  for (const p of pointsVehicule(kind, pos, rot)) {
    if (p.x < xMin || p.x > CARTE_X || p.z < 0 || p.z > CARTE_Z) return false
  }
  return true
}

/**
 * L'invariant de milieu, sur tous les points de sondage.
 *
 * UN AVION N'EN A PAS, et c'est voulu: on survole la mer comme la montagne,
 * c'est meme tout l'interet d'un avion. Ce qui le contraint n'est pas le sol
 * sous lui mais la PISTE — il ne touche terre que la (shared/sim/avion.ts).
 * Le serveur applique cette meme fonction a bord (`accepterABord`): il ne
 * gagnerait rien a refuser a un avion d'etre au-dessus de l'eau.
 */
export function milieuValide(kind: VehiculeKind, pos: Vec2, rot: number, terrain: ITerrain): boolean {
  const milieu = VEHICULES[kind].milieu
  if (milieu === 'air') return true
  const veutEau = milieu === 'eau'
  for (const p of pointsVehicule(kind, pos, rot)) {
    if (terrain.estEau(p.x, p.z) !== veutEau) return false
  }
  return true
}

/**
 * LE predicat de position. Tout deplacement de vehicule passe par ici, sans
 * exception: c'est ce qui rend l'invariant de milieu impossible a contourner.
 *
 * `bloque` est facultatif et vient du client: c'est ce qui arrete net une
 * voiture contre un arbre, un rocher ou un mur (voir `OptionsVehicule`). La
 * simulation pure n'a pas d'opinion sur ce qui encombre le terrain.
 */
export function positionValide(
  kind: VehiculeKind,
  pos: Vec2,
  rot: number,
  terrain: ITerrain,
  bloque?: (x: number, z: number) => boolean,
): boolean {
  if (!dansLeMonde(kind, pos, rot)) return false
  if (!milieuValide(kind, pos, rot, terrain)) return false
  if (bloque) {
    for (const p of pointsVehicule(kind, pos, rot)) {
      if (bloque(p.x, p.z)) return false
    }
  }
  return true
}

/**
 * Hauteur du plancher du vehicule: le sol pour la voiture, la mer pour la
 * barque, et le sol AUSSI pour un avion — c'est `majAvion` qui y ajoute
 * ensuite son altitude, parce que lui seul sait s'il vole.
 */
export function hauteurVehicule(kind: VehiculeKind, pos: Vec2, terrain: ITerrain): number {
  if (VEHICULES[kind].milieu === 'eau') return SEA_LEVEL
  return terrain.hauteur(pos.x, pos.z)
}

/** Multiplicateur de vitesse a appliquer au pilote (voiture x3, barque x2). */
export function multiplicateurVitesse(v: Vehicule): number {
  return VEHICULES[v.kind].vitesse
}

// ------------------------------------------------------------------ validation

/** "Il faut 20 bois et 4 pierre" a partir de ce qui manque. */
function phraseManquant(trou: Cout): string {
  const morceaux: string[] = []
  for (const cle of Object.keys(trou) as ItemId[]) {
    const n = trou[cle] ?? 0
    if (n > 0) morceaux.push(`${n} ${OBJETS[cle].nom.toLowerCase()}`)
  }
  if (morceaux.length === 0) return 'Il manque des ressources'
  if (morceaux.length === 1) return `Il faut ${morceaux[0]}`
  const dernier = morceaux.pop() as string
  return `Il faut ${morceaux.join(', ')} et ${dernier}`
}

/**
 * Placement d'un vehicule. Meme forme et meme esprit que `validerPlacement` des
 * batiments, pour que le menu de construction n'ait qu'un seul comportement.
 *
 * `batiments` sert a ne pas garer une voiture dans le salon; on traite le
 * vehicule comme un disque de rayon `rayonVehicule`, ce qui est conservateur et
 * suffit: on ne veut pas coller une carrosserie contre un mur.
 */
/**
 * LA PISTE SOUS UN AVION QU'ON POSE, et l'aeroport qui va avec.
 *
 * C'est la seule dependance d'un vehicule a un batiment de tout le jeu, et elle
 * est voulue: « il peut construire un avion aussi (doit etre construit a un
 * aeroport) ». Sans piste sous les roues, un avion neuf serait un avion qu'on
 * ne peut ni faire decoller ni reposer.
 */
export function pisteSousAvion(
  batiments: readonly Batiment[],
  pos: Vec2,
  marge = 0,
): { batiment: Batiment; piste: Piste } | null {
  for (const b of batiments) {
    const piste = pisteDe(b)
    if (!piste) continue
    if (surLaPiste(piste, pos.x, pos.z, marge)) return { batiment: b, piste }
  }
  return null
}

/**
 * OU L'AVION SE POSE VRAIMENT quand on le batit: sur l'axe, dans le sens du
 * decollage, un peu apres le seuil.
 *
 * On ne le laisse pas la ou le doigt a touche: un appareil de travers sur sa
 * piste devrait faire demi-tour pour partir, et un appareil a cheval sur le
 * bord roulerait dans l'herbe. Le geste reste « touche la piste »; c'est la
 * pose qui range.
 */
export function poseSurPiste(piste: Piste, pos: Vec2): { pos: Vec2; rot: number } {
  const r = reperePiste(piste, pos.x, pos.z)
  const long = Math.max(RECUL_PARKING, Math.min(piste.longueur - 6, r.long))
  return { pos: pointPiste(piste, long, 0), rot: piste.cap }
}

export function validerPlacementVehicule(
  kind: VehiculeKind,
  pos: Vec2,
  rot: number,
  terrain: ITerrain,
  vehicules: Vehicule[],
  batiments: Batiment[],
  inventaire: Inventaire,
  posJoueur?: Vec2,
): ValidationVehicule {
  const def = VEHICULES[kind]

  /*
   * UN AVION SE BATIT SUR UNE PISTE, et c'est la premiere chose qu'on verifie:
   * inutile de parler de pente ou de voisinage si l'endroit n'est pas un
   * aeroport. Le message dit quoi faire — « il faut d'abord un aeroport » est
   * une information, « placement impossible » n'en est pas une.
   */
  if (def.milieu === 'air') {
    const sous = pisteSousAvion(batiments, pos, 1)
    if (!sous) {
      return {
        ok: false,
        raison: batiments.some((b) => pisteDe(b) !== null)
          ? 'Un avion se bâtit sur la piste d’un aéroport'
          : 'Il faut d’abord bâtir un aéroport',
      }
    }
  }

  // 1. On ne pose pas un vehicule sur quelqu'un.
  if (posJoueur && dansEmpriseVehicule(kind, pos, rot, posJoueur.x, posJoueur.z, PLAYER_RADIUS + 0.25)) {
    return { ok: false, raison: 'Tu es à cet endroit : écarte-toi d’abord' }
  }

  // 2. Dans le monde, emprise comprise.
  if (!dansLeMonde(kind, pos, rot)) return { ok: false, raison: 'Hors du monde' }

  // 3. Le milieu. C'est la raison d'etre des deux vehicules.
  if (!milieuValide(kind, pos, rot, terrain)) {
    return def.milieu === 'eau'
      ? { ok: false, raison: 'Une barque se met à l’eau : vise la mer ou un lac' }
      : { ok: false, raison: 'Une voiture ne roule pas dans l’eau' }
  }

  // 4. Une voiture se pose a plat. La barque flotte: la question ne se pose pas.
  if (def.milieu === 'terre') {
    const points = pointsVehicule(kind, pos, rot)
    let somme = 0
    let hMin = Infinity
    let hMax = -Infinity
    for (const p of points) {
      const ech = terrain.echantillon(p.x, p.z)
      somme += ech.pente
      if (ech.hauteur < hMin) hMin = ech.hauteur
      if (ech.hauteur > hMax) hMax = ech.hauteur
    }
    if (somme / points.length > PENTE_POSE_MAX || hMax - hMin > DENIVELE_POSE_MAX) {
      return { ok: false, raison: 'Terrain trop en pente pour une voiture' }
    }
  }

  // 5. Pas sur un autre vehicule.
  const rayon = rayonVehicule(kind)
  for (const autre of vehicules) {
    const seuil = rayon + rayonVehicule(autre.kind) + MARGE_VEHICULE
    if (Math.hypot(pos.x - autre.pos.x, pos.z - autre.pos.z) < seuil) {
      return { ok: false, raison: 'Trop près d’un autre véhicule' }
    }
  }

  // 6. Pas dans un batiment.
  for (const b of batiments) {
    if (dansEmprise(b, pos.x, pos.z, rayon + MARGE_BATIMENT)) {
      return { ok: false, raison: 'Trop près d’un bâtiment' }
    }
  }

  // 7. De quoi payer.
  if (!peutPayer(inventaire, def.cout)) {
    return { ok: false, raison: phraseManquant(manquant(inventaire, def.cout)) }
  }

  return { ok: true }
}

// ------------------------------------------------------------------- creation

/**
 * Identifiant stable, sans horloge et sans hasard: hachage de kind + position +
 * rotation + proprietaire, exactement comme `idBatiment`. Le client et le
 * serveur calculent donc le meme id pour la meme pose, sans se parler.
 */
export function idVehicule(kind: VehiculeKind, pos: Vec2, rot: number, proprietaire: string): string {
  const gx = Math.round(pos.x * 100)
  const gz = Math.round(pos.z * 100)
  const grot = Math.round(rot * 1000)
  let graine = 0x9e3779b9
  for (let i = 0; i < proprietaire.length; i++) {
    graine = Math.imul(graine ^ proprietaire.charCodeAt(i), 16777619) >>> 0
  }
  for (let i = 0; i < kind.length; i++) {
    graine = Math.imul(graine ^ kind.charCodeAt(i), 16777619) >>> 0
  }
  const h1 = Math.floor(hache2(gx, gz, graine) * 0xffffffff) >>> 0
  const h2 = Math.floor(hache2(gz + grot, gx, graine ^ 0x51ed270b) * 0xffffffff) >>> 0
  return `v_${h1.toString(36)}${h2.toString(36)}`
}

/**
 * Fabrique le vehicule pose.
 *
 * `proprietaire` est le jeton, comme pour un batiment. `compte` est le NOM du
 * compte: c'est lui qui porte la couleur (shared/data/proprietaires.ts). Sans
 * `compte`, on retombe sur la couleur des invites.
 *
 * Une barque nait A L'ANCRE: sinon elle commencerait a deriver dans la seconde
 * qui suit sa construction, avant meme qu'on ait pu monter dedans.
 */
export function creerVehicule(
  kind: VehiculeKind,
  pos: Vec2,
  rot: number,
  proprietaire: string,
  terrain: ITerrain,
  compte?: string | null,
): Vehicule {
  return {
    id: idVehicule(kind, pos, rot, proprietaire),
    kind,
    pos: { x: pos.x, z: pos.z },
    y: hauteurVehicule(kind, pos, terrain),
    rot,
    proprietaire,
    couleur: couleurCompte(compte ?? null),
    ancre: VEHICULES[kind].milieu === 'eau',
    // Un vehicule neuf est OUVERT: on ne verrouille pas ce qu'on vient de
    // batir, on le prete. Le verrou est un geste, pas un etat par defaut.
    verrouille: false,
  }
}

// -------------------------------------------------------------- monter, sortir

/** Le vehicule le plus proche du point, ou null. Sert a proposer de monter. */
export function vehiculeProche(vehicules: Vehicule[], x: number, z: number, portee = REACH): Vehicule | null {
  let meilleur: Vehicule | null = null
  let meilleureD = portee
  for (const v of vehicules) {
    const d = distanceVehicule(v, x, z)
    if (d <= meilleureD) {
      meilleureD = d
      meilleur = v
    }
  }
  return meilleur
}

/**
 * Ce que la place du conducteur exige EN PLUS d'etre a portee.
 *
 * Ces deux champs disent la meme chose sous deux formes: la place est-elle
 * libre, et m'est-elle ouverte. Ils sont optionnels parce que le solo n'a ni
 * jeton ni voisins — et parce qu'un appelant qui les oublie doit se comporter
 * comme avant, jamais refuser au hasard.
 */
export interface ContexteMontee {
  /** Jeton de celui qui monte. Sert au verrou: seul le proprietaire passe. */
  jeton?: string | null
  /** Identifiants des vehicules qu'un AUTRE joueur pilote deja. */
  occupes?: ReadonlySet<string>
  /**
   * Vrai si CET appareil n'est pas gare (il roule, il vole).
   *
   * Un avion en vol passe au-dessus de tout: sans ce drapeau, un bonhomme
   * debout sous sa trajectoire serait « a portee » de la coque (la distance se
   * mesure a plat) et pourrait monter dedans en plein ciel. L'etat du vol vit
   * dans `shared/sim/avion.ts`, que ce module n'a pas a connaitre: on lui passe
   * donc la reponse, pas la question.
   */
  enVol?: boolean
}

/**
 * Vrai si ce jeton est celui du proprietaire du vehicule.
 *
 * Une fonction pour la question, appelee des deux cotes du fil: le client s'en
 * sert pour proposer le verrou, le serveur pour l'accorder. Un jeton vide n'est
 * proprietaire de rien — un invite ne verrouille pas la voiture des autres.
 */
export function estProprietaire(v: Vehicule, jeton: string | null | undefined): boolean {
  return typeof jeton === 'string' && jeton.length > 0 && jeton === v.proprietaire
}

/**
 * Monter a bord.
 *
 * On ne demande toujours pas d'etre le proprietaire: le monde est familial, on
 * se prete la barque. Deux choses ont ete ajoutees, et elles repondent a deux
 * bogues distincts d'une meme partie:
 *
 * 1. UNE SEULE PERSONNE AU VOLANT. Rien ne comptait les occupants: deux
 *    joueurs ont pilote LA MEME voiture, chacun la tirant vers son propre
 *    joystick, et le vehicule sautait d'une position a l'autre a chaque trame.
 *    Ce n'est pas un partage, c'est une voiture qui vibre.
 * 2. LE VERROU. Le proprietaire peut fermer la sienne; elle ne s'ouvre alors
 *    plus qu'a lui.
 *
 * Aucune des deux n'est une politesse: le serveur les rejoue (voir
 * `refuserLeVolant` dans server/salon.ts), parce qu'une regle qui ne vit que
 * dans le navigateur n'est pas une regle.
 */
export function peutMonter(
  v: Vehicule,
  joueur: Joueur,
  portee = REACH,
  ctx: ContexteMontee = {},
): ValidationVehicule {
  if (joueur.vehicule === v.id) return { ok: false, raison: 'Tu es déjà à bord' }
  if (joueur.vehicule !== null) return { ok: false, raison: 'Descends d’abord de ton véhicule' }
  if (joueur.etat === 'dort') return { ok: false, raison: 'Pas en dormant' }
  if (ctx.occupes?.has(v.id)) return { ok: false, raison: 'Quelqu’un est déjà au volant' }
  if (ctx.enVol === true) return { ok: false, raison: 'Cet avion est en vol' }
  if (v.verrouille && !estProprietaire(v, ctx.jeton)) {
    return { ok: false, raison: '🔒 Verrouillé : demande à son propriétaire' }
  }
  if (distanceVehicule(v, joueur.pos.x, joueur.pos.z) > portee) {
    return { ok: false, raison: 'Trop loin : approche-toi du véhicule' }
  }
  return { ok: true }
}

/**
 * Ou le pilote peut mettre pied a terre: on sonde un anneau autour du vehicule
 * et on garde le point le moins pentu qui soit de la terre ferme.
 *
 * Rend null s'il n'y a que de l'eau ou de la falaise autour: c'est ce null qui
 * interdit de sortir d'une voiture au bord d'un precipice, et de quitter une
 * barque en pleine mer autrement qu'a la nage.
 */
export function pointDebarquement(v: Vehicule, terrain: ITerrain): Vec2 | null {
  const rayon = rayonVehicule(v.kind) + PORTEE_DEBARQUEMENT
  let meilleur: Vec2 | null = null
  let meilleurePente = Infinity
  for (let i = 0; i < DIRECTIONS_DEBARQUEMENT; i++) {
    const a = v.rot + (i / DIRECTIONS_DEBARQUEMENT) * Math.PI * 2
    const x = v.pos.x + Math.sin(a) * rayon
    const z = v.pos.z + Math.cos(a) * rayon
    if (x < PLAYER_RADIUS || x > CARTE_X - PLAYER_RADIUS) continue
    if (z < PLAYER_RADIUS || z > CARTE_Z - PLAYER_RADIUS) continue
    if (terrain.estEau(x, z)) continue
    const pente = terrain.echantillon(x, z).pente
    if (pente > PENTE_DEBARQUEMENT_MAX) continue
    if (pente < meilleurePente) {
      meilleurePente = pente
      meilleur = { x, z }
    }
  }
  return meilleur
}

/**
 * Descendre.
 *
 * Voiture: il faut un sol praticable a cote. Au bord d'une falaise, la reponse
 * est non — on remonte la piste et on sort ailleurs.
 *
 * Barque: si la rive est a portee, on debarque dessus. Sinon on est en pleine
 * mer et il faut se mettre a l'eau, donc nager: on l'autorise SEULEMENT si
 * l'ancre est jetee, faute de quoi la barque deriverait pendant qu'on nage et on
 * la perdrait. Jeter l'ancre est donc aussi ce qui permet de se baigner.
 */
export function peutDescendre(v: Vehicule, terrain: ITerrain, enVol = false): ValidationVehicule {
  /*
   * ON NE DESCEND PAS D'UN AVION EN VOL, et c'est la seule regle du genre dans
   * tout le jeu. Elle est aussi la reponse a la commande « le bonhomme entre et
   * sort de l'avion par l'aeroport ou l'avion se trouve »: un appareil ne se
   * pose que sur une piste, donc on n'en sort qu'a un aeroport, sans qu'il
   * faille l'ecrire nulle part ailleurs.
   */
  if (enVol) return { ok: false, raison: 'Pose-toi d’abord : touche un aéroport' }
  const terre = pointDebarquement(v, terrain)
  if (terre) return { ok: true }
  if (VEHICULES[v.kind].milieu === 'terre') {
    return { ok: false, raison: 'Impossible de sortir ici : ça tombe trop raide' }
  }
  if (!v.ancre) return { ok: false, raison: 'Jette l’ancre avant de te mettre à l’eau' }
  return { ok: true }
}

/**
 * Ou deposer le pilote qui descend: la rive si elle existe, sinon a cote de la
 * barque, a la nage. A n'appeler qu'apres un `peutDescendre` favorable.
 */
export function positionDescente(v: Vehicule, terrain: ITerrain): Vec2 {
  const terre = pointDebarquement(v, terrain)
  if (terre) return terre
  const decale = tournerLibre(rayonVehicule(v.kind) * 0.8, 0, v.rot)
  return { x: v.pos.x + decale.x, z: v.pos.z + decale.z }
}

// -------------------------------------------------------------------- pilotage

export interface MajVehicule {
  /** Vrai si le vehicule n'a pas pu suivre son pilote (mauvais milieu, bord du monde, ancre, obstacle). */
  bloque: boolean
  /** Position du vehicule apres la mise a jour: c'est LA du pilote, s'il est a bord. */
  pos: Vec2
  /** Position AVANT la mise a jour: avec `pos`, donne le segment parcouru. */
  depart: Vec2
  /** Hauteur du plancher du vehicule. */
  y: number
  /** Vitesse instantanee en m/s: sert au sillage, aux roues et au son. */
  vitesse: number
  /** Largeur du passage, en metres: la voie du vehicule. */
  largeur: number
}

export interface OptionsVehicule {
  /**
   * Vrai si (x, z) est infranchissable. Le client y branche sa grille de
   * navigation: un arbre, un rocher ou un mur arrete la voiture NET, exactement
   * comme le rivage, et rien de construit n'est jamais abime puisque le vehicule
   * ne peut pas y entrer.
   */
  bloque?: (x: number, z: number) => boolean
  /**
   * Appele le long du segment reellement parcouru, environ tous les
   * PAS_PASSAGE metres. C'est le crochet qui permet de coucher l'herbe et les
   * fleurs sans que ce module connaisse quoi que ce soit au decor.
   */
  onPassage?: (x: number, z: number, largeur: number) => void
}

/** Pas d'echantillonnage du crochet `onPassage`, en metres. */
export const PAS_PASSAGE = 0.5

/**
 * Angle de derive d'une barque: tire de son identifiant, donc stable d'un bout a
 * l'autre de la partie et identique sur le client et sur le serveur. Pas de
 * Math.random, pas d'horloge.
 */
export function angleDerive(v: Vehicule): number {
  let graine = 0x811c9dc5
  for (let i = 0; i < v.id.length; i++) {
    graine = Math.imul(graine ^ v.id.charCodeAt(i), 16777619) >>> 0
  }
  return hache2(graine & 0xffff, graine >>> 16, 0x2f6a5d) * Math.PI * 2
}

/**
 * Un pas de simulation du vehicule.
 *
 * A bord: le vehicule suit la position et l'orientation du pilote, mais
 * uniquement la ou son milieu l'autorise. Si la cible est interdite, on essaie
 * de glisser sur un seul axe (comme le bonhomme contre un mur), et sinon on ne
 * bouge pas: `bloque` vaut alors vrai. Le pilote doit dans tous les cas etre
 * ramene sur `pos` — c'est ainsi qu'une voiture ne rentre jamais dans l'eau
 * meme si le joueur pousse le joystick vers la mer.
 *
 * A l'ancre, une barque ne bouge plus du tout, meme avec quelqu'un dedans:
 * c'est ce qui la tient en place pendant qu'on peche.
 *
 * A vide: une barque non ancree derive TRES lentement, une voiture ne bouge pas.
 */
export function majVehicule(
  v: Vehicule,
  dt: number,
  joueur: Joueur | null,
  terrain: ITerrain,
  options?: OptionsVehicule,
): MajVehicule {
  const def = VEHICULES[v.kind]
  const bloqueur = options?.bloque
  const departX = v.pos.x
  const departZ = v.pos.z
  const aBord = joueur !== null && joueur.vehicule === v.id
  let bloque = false

  /*
   * UN AVION NE SUIT PAS SON PILOTE: c'est lui qui emmene le pilote.
   *
   * Tout ce qui suit — « le vehicule est ramene sous le bonhomme, sauf la ou
   * son milieu l'interdit » — est la mecanique d'une voiture et d'une barque,
   * et elle n'a aucun sens en l'air: un avion ne s'arrete pas, ne recule pas et
   * ne tourne pas sur place. Son pas est ailleurs (`majAvion`), appele par le
   * jeu AVANT celui-ci; ici on ne fait que rendre compte de sa position, pour
   * que le pilote y soit assis et que le rendu suive.
   */
  if (def.milieu === 'air') {
    const parcouru = Math.hypot(v.pos.x - departX, v.pos.z - departZ)
    return {
      bloque: false,
      pos: { x: v.pos.x, z: v.pos.z },
      depart: { x: departX, z: departZ },
      y: v.y,
      vitesse: dt > 1e-6 ? parcouru / dt : 0,
      largeur: def.taille.l,
    }
  }

  if (aBord && joueur) {
    const rot = joueur.rot
    const cibleX = joueur.pos.x
    const cibleZ = joueur.pos.z
    const ancree = v.ancre && def.milieu === 'eau'
    if (ancree) {
      bloque = Math.abs(cibleX - v.pos.x) > 1e-6 || Math.abs(cibleZ - v.pos.z) > 1e-6
    } else if (positionValide(v.kind, { x: cibleX, z: cibleZ }, rot, terrain, bloqueur)) {
      v.pos.x = cibleX
      v.pos.z = cibleZ
      v.rot = rot
    } else if (positionValide(v.kind, { x: cibleX, z: v.pos.z }, rot, terrain, bloqueur)) {
      // Glissement sur un seul axe: on longe la rive au lieu de coller net.
      v.pos.x = cibleX
      v.rot = rot
      bloque = true
    } else if (positionValide(v.kind, { x: v.pos.x, z: cibleZ }, rot, terrain, bloqueur)) {
      v.pos.z = cibleZ
      v.rot = rot
      bloque = true
    } else {
      // Meme immobile, on laisse tourner sur place si l'emprise tournee tient.
      if (positionValide(v.kind, v.pos, rot, terrain, bloqueur)) v.rot = rot
      bloque = true
    }
  } else if (def.milieu === 'eau' && !v.ancre) {
    const a = angleDerive(v)
    const pas = DERIVE_VITESSE * dt
    const cible = { x: v.pos.x + Math.sin(a) * pas, z: v.pos.z + Math.cos(a) * pas }
    // La barque qui derive s'arrete d'elle-meme contre la rive: la position
    // n'est acceptee que si elle respecte l'invariant de milieu.
    if (positionValide(v.kind, cible, v.rot, terrain, bloqueur)) {
      v.pos.x = cible.x
      v.pos.z = cible.z
    }
  }

  v.y = hauteurVehicule(v.kind, v.pos, terrain)
  const parcouru = Math.hypot(v.pos.x - departX, v.pos.z - departZ)
  const largeur = def.taille.l

  // Trace laissee derriere soi. On echantillonne le segment au lieu de ne
  // signaler que le point d'arrivee: a 21 m/s une voiture avance d'un metre par
  // tick, et la trace serait pointillee.
  if (options?.onPassage && parcouru > 1e-4) {
    const n = Math.max(1, Math.ceil(parcouru / PAS_PASSAGE))
    for (let i = 1; i <= n; i++) {
      const t = i / n
      options.onPassage(departX + (v.pos.x - departX) * t, departZ + (v.pos.z - departZ) * t, largeur)
    }
  }

  return {
    bloque,
    pos: { x: v.pos.x, z: v.pos.z },
    depart: { x: departX, z: departZ },
    y: v.y,
    vitesse: dt > 1e-6 ? parcouru / dt : 0,
    largeur,
  }
}

// ----------------------------------------------------------- ancre et peche

/** Vrai si ce vehicule a une ancre (donc: la barque). */
export function peutJeterAncre(v: Vehicule): boolean {
  return VEHICULES[v.kind].milieu === 'eau'
}

/**
 * Jette ou releve l'ancre. Rend le nouvel etat (`true` = mouille).
 * Sur une voiture, ne fait rien et rend `false`.
 */
export function basculerAncre(v: Vehicule): boolean {
  if (!peutJeterAncre(v)) return false
  v.ancre = !v.ancre
  return v.ancre
}

/**
 * On peche depuis un vehicule qui s'y prete, a l'ancre, et sur de l'eau.
 *
 * L'ancre n'est pas un caprice: sans elle la barque derive pendant les 2.6 s de
 * l'action, et la ligne suivrait le bateau. C'est aussi ce qui donne un sens au
 * geste demande par le proprietaire — jeter l'ancre, puis pecher.
 */
export function peutPecherDepuis(v: Vehicule, terrain: ITerrain): boolean {
  if (!VEHICULES[v.kind].peutPecher) return false
  if (!v.ancre) return false
  return terrain.estEau(v.pos.x, v.pos.z)
}

/** Point d'eau vise par la ligne: devant la proue. Sert a viser et a animer. */
export function pointPecheVehicule(v: Vehicule): Vec2 {
  const t = VEHICULES[v.kind].taille
  const d = tournerLibre(0, t.p / 2 + 1.2, v.rot)
  return { x: v.pos.x + d.x, z: v.pos.z + d.z }
}

// ------------------------------------------------------- peche a la ligne

/**
 * La peche est un petit jeu d'adresse: on lance, on attend, le flotteur plonge,
 * et il faut ferrer dans la seconde. Tout est ici, en pur: le son, l'affichage et
 * le geste du joueur restent au client.
 *
 * Le hasard passe par `Alea` (jamais Math.random) et le temps par des `dt`
 * (jamais Date.now): la meme graine rejoue exactement la meme partie de peche,
 * sur le client comme sur le serveur.
 */
export type PhasePeche = 'attente' | 'touche' | 'ratee' | 'prise'

/** Bornes du delai avant la touche, en secondes. */
export const DELAI_TOUCHE_MIN = 2
export const DELAI_TOUCHE_MAX = 6

/** Duree de la fenetre pour ferrer, en secondes. */
export const FENETRE_FERRAGE = 1

/** Au-dela de cette profondeur d'eau, on est au large. */
export const PROFONDEUR_LARGE = 2.5

export interface EtatPeche {
  phase: PhasePeche
  /** Secondes restantes avant que le flotteur plonge. */
  delai: number
  /** Secondes restantes pour ferrer, une fois la touche donnee. */
  fenetre: number
  /** Ou flotte la ligne. Decide de la prise. */
  flotteur: Vec2
  /** Ce que le lieu rendra si on ferre a temps. Fixe au lancer. */
  prise: ItemId
  /** Vrai si l'on peche au mouillage: ca mord plus souvent. */
  ancre: boolean
}

export interface EvenementPeche {
  phase: PhasePeche
  /** Vrai UNIQUEMENT au pas ou le flotteur plonge: c'est la que part le son. */
  mord: boolean
  /** Vrai UNIQUEMENT au pas ou le poisson vient de filer. */
  file: boolean
}

export interface ResultatPeche {
  ok: boolean
  /** L'objet gagne, ou null. */
  prise: ItemId | null
  /** Message francais affichable tel quel. */
  raison?: string
}

/** Profondeur d'eau sous un point, en metres. 0 des qu'on est au sec. */
export function profondeurEau(terrain: ITerrain, x: number, z: number): number {
  if (!terrain.estEau(x, z)) return 0
  return Math.max(0, SEA_LEVEL - terrain.hauteur(x, z))
}

/**
 * Ce qui mord ici. Le lieu decide, pas le hasard: pres du bord un petit poisson,
 * au large un gros, et devant le glacier le poisson des glaces. C'est ce qui
 * donne une raison d'aller loin en barque.
 */
/**
 * Les trois seules prises possibles.
 *
 * Le serveur s'en sert pour BORNER ce qu'un client peut annoncer avoir peche:
 * le sac est tenu la-bas (server/inventaire.ts), et sans cette liste « j'ai
 * peche un minerai » aurait ete une phrase acceptable.
 */
export const PRISES: readonly ItemId[] = ['poisson', 'gros_poisson', 'poisson_rare']

export function priseAuPoint(flotteur: Vec2, terrain: ITerrain): ItemId {
  if (terrain.biome(flotteur.x, flotteur.z) === 'glacier') return 'poisson_rare'
  return profondeurEau(terrain, flotteur.x, flotteur.z) >= PROFONDEUR_LARGE ? 'gros_poisson' : 'poisson'
}

/**
 * Delai avant la touche.
 *
 * A l'ancre on tire deux fois et on garde le plus court: ca mord plus souvent
 * (delai moyen 3.3 s au lieu de 4 s) sans jamais sortir des bornes annoncees au
 * joueur, 2 a 6 secondes.
 */
function tirerDelai(alea: Alea, ancre: boolean): number {
  const a = alea.entre(DELAI_TOUCHE_MIN, DELAI_TOUCHE_MAX)
  if (!ancre) return a
  const b = alea.entre(DELAI_TOUCHE_MIN, DELAI_TOUCHE_MAX)
  return Math.min(a, b)
}

/**
 * Lance la ligne. `ancre` vaut `peutPecherDepuis(barque, terrain)` en barque, et
 * faux depuis la berge.
 */
export function commencerPeche(flotteur: Vec2, terrain: ITerrain, alea: Alea, ancre = false): EtatPeche {
  return {
    phase: 'attente',
    delai: tirerDelai(alea, ancre),
    fenetre: 0,
    flotteur: { x: flotteur.x, z: flotteur.z },
    prise: priseAuPoint(flotteur, terrain),
    ancre,
  }
}

/** Un pas de la peche. A appeler a chaque tick tant que `pecheFinie` est faux. */
export function majPeche(e: EtatPeche, dt: number): EvenementPeche {
  let mord = false
  let file = false
  if (e.phase === 'attente') {
    e.delai -= dt
    if (e.delai <= 0) {
      e.delai = 0
      e.phase = 'touche'
      e.fenetre = FENETRE_FERRAGE
      mord = true
    }
  } else if (e.phase === 'touche') {
    e.fenetre -= dt
    if (e.fenetre <= 0) {
      e.fenetre = 0
      e.phase = 'ratee'
      file = true
    }
  }
  return { phase: e.phase, mord, file }
}

/**
 * Ferre. Trop tot, le poisson se mefie et la ligne est bonne a relancer; dans la
 * fenetre, c'est une prise; trop tard, il a file.
 */
export function ferrer(e: EtatPeche): ResultatPeche {
  if (e.phase === 'attente') {
    e.phase = 'ratee'
    return { ok: false, prise: null, raison: 'Trop tôt : le poisson s’est méfié' }
  }
  if (e.phase === 'touche') {
    e.phase = 'prise'
    return { ok: true, prise: e.prise }
  }
  if (e.phase === 'ratee') return { ok: false, prise: null, raison: 'Trop tard : le poisson a filé' }
  return { ok: false, prise: null, raison: 'La ligne est déjà remontée' }
}

/** Vrai quand il n'y a plus rien a attendre: on peut relancer. */
export function pecheFinie(e: EtatPeche): boolean {
  return e.phase === 'ratee' || e.phase === 'prise'
}

// ------------------------------------------------------------- persistance

function estFini(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n)
}

export function estVehiculeKind(x: unknown): x is VehiculeKind {
  return typeof x === 'string' && Object.prototype.hasOwnProperty.call(VEHICULES, x)
}

/**
 * Relit un vehicule venu d'un JSON (sauvegarde du serveur, message reseau).
 *
 * Rend null au lieu de jeter: une ligne abimee en base doit faire disparaitre un
 * vehicule, pas empecher la partie de demarrer. Les champs sont recopies un a
 * un, jamais l'objet d'origine, pour qu'aucune propriete etrangere ne se glisse
 * dans la simulation.
 */
export function validerVehicule(v: unknown): Vehicule | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  if (!estVehiculeKind(o['kind'])) return null
  if (typeof o['id'] !== 'string' || o['id'].length === 0) return null
  if (typeof o['proprietaire'] !== 'string') return null
  const pos = o['pos']
  if (typeof pos !== 'object' || pos === null) return null
  const p = pos as Record<string, unknown>
  if (!estFini(p['x']) || !estFini(p['z'])) return null
  if (!estFini(o['rot'])) return null
  const y = estFini(o['y']) ? o['y'] : 0
  const couleur = estFini(o['couleur']) ? Math.max(0, Math.min(0xffffff, Math.round(o['couleur']))) : couleurCompte(null)
  return {
    id: o['id'],
    kind: o['kind'],
    pos: { x: p['x'], z: p['z'] },
    y,
    rot: o['rot'],
    proprietaire: o['proprietaire'],
    couleur,
    ancre: o['ancre'] === true,
    verrouille: o['verrouille'] === true,
    ...(typeof o['retour'] === 'string' && o['retour'].length > 0 && o['retour'].length <= 64 ? { retour: o['retour'] } : {}),
  }
}
